
import { Character, Dogma, VideoProvider } from '../types';

interface CompileContext {
    characters: Character[];
    dogma: Dogma | null;
}

/**
 * Compiles a raw prompt into a provider-ready prompt.
 * Crucially, detects character mentions (e.g. "@Sophie") and replaces them 
 * with proper visual descriptions to ensure character consistency without relying 
 * on implicit knowledge.
 */
export const compilePromptForProvider = (
    rawPrompt: string,
    context: CompileContext,
    provider: VideoProvider
): string => {
    let compiledPrompt = rawPrompt;
    const compiledCharacters: string[] = [];

    // Normalization helper
    const normalize = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    // 1. Character Injection
    if (context.characters.length > 0) {
        const sortedChars = [...context.characters].sort((a, b) => b.name.length - a.name.length); // Match longest names first

        sortedChars.forEach(char => {
            // Escape special regex characters in the character name
            const escapedName = char.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Match @Name (case insensitive, word boundary)
            const regex = new RegExp(`@${escapedName}\\b`, 'gi');

            // Check if present
            if (regex.test(compiledPrompt)) {
                compiledCharacters.push(char.name);

                const descriptionToInject = char.description
                    ? `${char.name} (${char.description})`
                    : `${char.name} (character)`;

                // Replace all occurrences
                compiledPrompt = compiledPrompt.replace(new RegExp(`@${escapedName}\\b`, 'gi'), descriptionToInject);
            }
        });
    }

    // 2. Dogma Injection (Optional, if provider needs it explicitly in prompt)
    // Veo/Gemini usually handles this via System Instruction.
    // LTX might benefit from a style suffix.
    if (provider === VideoProvider.LTX && context.dogma) {
        // Add dogma style keywords if not present
        // compiledPrompt += ` --style ${context.dogma.title}`; 
    }

    // Structured logging (no sensitive data)
    if (compiledCharacters.length > 0) {
        console.log(`[PROMPT] compiledCharacters=[${compiledCharacters.join(',')}] provider=${provider}`);
    }

    return compiledPrompt;
};
